import React from 'react';

import Image from '@atlaskit/image';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import AddonIcon from '@atlaskit/icon/core/app';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type ModalIframeRendererProps } from '../types';
import { getExtensionIcon } from '../custom-ui-modal-utils';

export type ModalHeaderProps = {
	closeModalExtension?: () => void;
	executeViewOnCloseCallback: () => Promise<void>;
	extension: ModalIframeRendererProps['extension'];
	icon?: string;
	isBridgeModal: boolean;
	isMacroExtension: boolean;
	modalExtension?: ModalIframeRendererProps['modalExtension'];
	modalSize: string;
	onClose: () => Promise<void>;
	title?: string;
};

export const ModalCustomUIRendererHeader = ({
	isBridgeModal,
	isMacroExtension,
	modalSize,
	title,
	icon,
	extension,
	modalExtension,
	onClose,
	closeModalExtension,
	executeViewOnCloseCallback,
}: ModalHeaderProps): React.JSX.Element => {
	/* Title/icon source priority:
	 * - Bridge modals:
	 *     - Use title/icon props (passed in from bridge call)
	 *     - Fallback to extension.properties.title for fullscreen modals with empty title
	 *     - For non-fullscreen modals, we won't render the header if the title prop is not provided
	 * - Modal extension points: Use modalExtension.title and extensionIcon, defaults to extension name if not provided
	 */
	const modalTitle = isBridgeModal
		? modalSize === 'fullscreen' && !title
			? extension.properties.title
			: title
		: modalExtension?.title || extension.properties.title;

	/**
	 * Icon rendering logic:
	 * - Bridge modals: show icon section only if icon prop is provided
	 * - Macro extension points: always show icon section (uses extension icon or AddonIcon fallback)
	 * - Other modals: no icon section
	 */
	const shouldShowIcon = isBridgeModal ? !!icon : isMacroExtension;
	const modalIcon = isBridgeModal ? icon : getExtensionIcon(extension);

	return (
		<ModalHeader>
			{shouldShowIcon ? (
				<Inline space="space.100" alignBlock="center">
					{modalIcon ? (
						<Image src={modalIcon} alt="" width="24px" height="24px" />
					) : (
						<AddonIcon label="" color={token('color.icon.accent.gray')} />
					)}
					<ModalTitle>{modalTitle}</ModalTitle>
				</Inline>
			) : (
				<ModalTitle>{modalTitle}</ModalTitle>
			)}
			<IconButton
				appearance="subtle"
				icon={CrossIcon}
				label="Close Modal"
				onClick={
					isBridgeModal
						? onClose
						: async () => {
								try {
									await executeViewOnCloseCallback();
								} finally {
									closeModalExtension?.();
								}
							}
				}
			/>
		</ModalHeader>
	);
};
